import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useRealmContext } from 'Providers/MongoRealmProvider/context';
import { useCallback, useMemo, useState } from 'react';
import { MONGO_REALM_COLLECTIONS } from 'Services/MongoRealm';
import { useSnackbar } from 'notistack';
import { toTitleCase } from '@setvi/shared/utils/formatCustomerData';
import { Customer } from 'Services/MongoRealm/Types';
import { Product } from '@setvi/shared/interfaces';

export const useProductDetailsPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [product, setProduct] = useState<Product>();
  const [customer, setCustomer] = useState<Customer>();
  const { companyData, user: appUser } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const userType = useMemo(
    () =>
      companyData?.CompanySettingsList?.find(
        setting => setting.CompanySettingsTypeID === 38
      )?.Value as CompanyProductUserType,
    [companyData]
  );
  const { user, client } = useRealmContext();

  const fetchProduct = useCallback(
    async (productId: Product['_id']) => {
      setIsLoading(true);
      try {
        const response = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.PRODUCTS)
          .findOne({ _id: productId });

        setProduct({
          ...response,
          name: toTitleCase(response.name),
          ...(response.vendor && {
            vendor: {
              ...response.vendor,
              name: toTitleCase(response.vendor.name)
            }
          }),
          price:
            userType === CompanyProductUserType.Manufacturer
              ? response.price
              : null
        });
      } catch (e) {
        enqueueSnackbar('Error occured while fetching product data.', {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    },
    [client, enqueueSnackbar, companyData, userType]
  );

  const fetchCustomer = useCallback(
    async (customerId: Customer['_id']) => {
      setIsLoading(true);
      try {
        const response: Customer = await client
          .db(
            companyData.Settings[CompanySettingsTypeID.AtlasMongoProductDBName]
              ?.Value
          )
          .collection(MONGO_REALM_COLLECTIONS.ACCOUNTS)
          .findOne({ _id: customerId });

        setCustomer(response);
      } catch (e) {
        enqueueSnackbar('Error occured while fetching customer data', {
          variant: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    },
    [client, enqueueSnackbar, companyData]
  );

  const updateProduct = (newValues: Partial<Product>) =>
    setProduct({ ...product, ...newValues });

  return {
    isLoading,
    product,
    customer,
    userType,
    user,
    appUser,
    client,
    fetchProduct,
    fetchCustomer,
    updateProduct
  };
};
